import type {
  JobcardManpowerAssignedLine,
  JobcardManpowerDraftTime,
  JobcardManpowerEmployee,
  JobcardManpowerRateBasis,
} from "../types/jobcardManpower";

const JOBCARD_MANPOWER_HOURS_PER_DAY = 8;
const JOBCARD_MANPOWER_DEFAULT_HOURLY_RATE = 350;

/** Client-side fallback when API does not return rate_basis. */
export function getCurrentMonthRateBasis(): JobcardManpowerRateBasis {
  const now = new Date();
  const monthDays = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const monthLabel = now.toLocaleString("en-IN", {
    month: "long",
    year: "numeric",
  });
  return {
    month_label: monthLabel,
    month_days: monthDays,
    hours_per_day: JOBCARD_MANPOWER_HOURS_PER_DAY,
    monthly_hours: 0,
    default_hourly_rate: JOBCARD_MANPOWER_DEFAULT_HOURLY_RATE,
    formula: "employee.cost_allocation_hourly",
  };
}

export function formatJobcardManpowerRateBasisLabel(
  basis: JobcardManpowerRateBasis
): string {
  return "Employee cost allocation (hourly)";
}

export const JOBCARD_MANPOWER_HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);
export const JOBCARD_MANPOWER_MINUTE_OPTIONS = [0, 15, 30, 45];

export function jobcardManpowerInitials(name: string): string {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

export function jobcardManpowerHasOvertime(d: JobcardManpowerDraftTime): boolean {
  return (d.overtime_hours || 0) > 0 || (d.overtime_minutes || 0) > 0;
}

export function jobcardManpowerLineMinutes(d: JobcardManpowerDraftTime): number {
  return (
    (d.regular_hours || 0) * 60 +
    (d.regular_minutes || 0) +
    (d.overtime_hours || 0) * 60 +
    (d.overtime_minutes || 0)
  );
}

/** OT hourly rate (editable). Falls back to regular rate when OT has no override. */
export function jobcardManpowerResolveOtRate(
  regularRate: number,
  d: JobcardManpowerDraftTime
): number {
  const regRate =
    regularRate > 0 ? regularRate : JOBCARD_MANPOWER_DEFAULT_HOURLY_RATE;
  if (!jobcardManpowerHasOvertime(d)) {
    return regRate;
  }
  const hasOverride =
    d.hourly_rate !== undefined && d.hourly_rate !== null;
  if (hasOverride) {
    const custom = Number(d.hourly_rate);
    if (!Number.isNaN(custom) && custom >= 0) return custom;
  }
  return regRate;
}

/** @deprecated Use jobcardManpowerResolveOtRate for OT display. */
export function jobcardManpowerResolveRate(
  defaultRate: number,
  d: JobcardManpowerDraftTime
): number {
  return jobcardManpowerResolveOtRate(defaultRate, d);
}

export function draftTimeFromAssignedLine(
  line: JobcardManpowerAssignedLine
): JobcardManpowerDraftTime {
  const hasOt =
    (line.overtime_hours || 0) > 0 || (line.overtime_minutes || 0) > 0;
  return {
    regular_hours: line.regular_hours,
    regular_minutes: line.regular_minutes,
    overtime_hours: line.overtime_hours,
    overtime_minutes: line.overtime_minutes,
    hourly_rate: hasOt ? line.man_hr_rate : undefined,
  };
}

/** Regular hours × regular rate + OT hours × OT rate. */
export function jobcardManpowerEstCost(
  regularRate: number,
  d: JobcardManpowerDraftTime
): number {
  const regMins =
    (d.regular_hours || 0) * 60 + (d.regular_minutes || 0);
  const otMins =
    (d.overtime_hours || 0) * 60 + (d.overtime_minutes || 0);
  const regRate =
    regularRate > 0 ? regularRate : JOBCARD_MANPOWER_DEFAULT_HOURLY_RATE;
  const otRate = jobcardManpowerResolveOtRate(regRate, d);
  const total = (regMins / 60) * regRate + (otMins / 60) * otRate;
  return Math.round(total * 100) / 100;
}

export function resolveRegularRateForAssignedLine(
  line: JobcardManpowerAssignedLine,
  employees: JobcardManpowerEmployee[],
  fallbackRate = JOBCARD_MANPOWER_DEFAULT_HOURLY_RATE
): number {
  const empId = String(line.employeeid || "")
    .trim()
    .toLowerCase();
  const emp = employees.find(
    (e) => String(e.id || "").trim().toLowerCase() === empId
  );
  if (emp) {
    const rate = Number(emp.hourly_rate);
    if (!Number.isNaN(rate) && rate > 0) return rate;
  }
  if (line.regular_hr_rate && line.regular_hr_rate > 0) {
    return line.regular_hr_rate;
  }
  return fallbackRate > 0 ? fallbackRate : JOBCARD_MANPOWER_DEFAULT_HOURLY_RATE;
}

/** Recompute line cost from regular + OT rates (fixes stale DB totals on reopen). */
export function recalculateAssignedLineAmounts(
  line: JobcardManpowerAssignedLine,
  regularRate: number
): JobcardManpowerAssignedLine {
  const draft = draftTimeFromAssignedLine(line);
  const hasOt = jobcardManpowerHasOvertime(draft);
  const regRate =
    regularRate > 0
      ? regularRate
      : line.regular_hr_rate && line.regular_hr_rate > 0
        ? line.regular_hr_rate
        : JOBCARD_MANPOWER_DEFAULT_HOURLY_RATE;
  const amount = jobcardManpowerEstCost(regRate, draft);
  const otRate = hasOt ? jobcardManpowerResolveOtRate(regRate, draft) : regRate;
  return {
    ...line,
    regular_hr_rate: regRate,
    man_hr_rate: hasOt ? otRate : regRate,
    amount,
    allocated_cost: amount,
  };
}

export function sumAssignedLineCosts(lines: JobcardManpowerAssignedLine[]): number {
  return Math.round(
    lines.reduce(
      (s, l) => s + (Number(l.allocated_cost) || Number(l.amount) || 0),
      0
    ) * 100
  ) / 100;
}

export function jobcardManpowerFormatMinutes(total: number): string {
  if (total <= 0) return "0 Min";
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m} Min`;
}

export function jobcardManpowerFormatCurrency(n: number): string {
  return `₹${(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function jobcardManpowerEmptyDraft(): JobcardManpowerDraftTime {
  return {
    regular_hours: 0,
    regular_minutes: 0,
    overtime_hours: 0,
    overtime_minutes: 0,
  };
}

export function jobcardManpowerSummaryFromLines(
  assigned: JobcardManpowerAssignedLine[],
  totalLabourCost: number | ""
) {
  let reg = 0;
  let ot = 0;
  let cost = 0;
  assigned.forEach((l) => {
    reg += (l.regular_hours || 0) * 60 + (l.regular_minutes || 0);
    ot += (l.overtime_hours || 0) * 60 + (l.overtime_minutes || 0);
    cost += Number(l.allocated_cost) || Number(l.amount) || 0;
  });
  const totalMins = reg + ot;
  // Summary cost = sum of line costs. Optional total_labour_cost is for save-time distribution only.
  void totalLabourCost;
  return {
    employees: assigned.length,
    timeLabel: jobcardManpowerFormatMinutes(totalMins),
    cost,
  };
}

export function normalizeAssignedLine(
  raw: Record<string, unknown>
): JobcardManpowerAssignedLine {
  const empId =
    raw.employeeid ??
    raw.employee_id ??
    raw.EmployeeId ??
    raw.employeeId ??
    "";
  return {
    employeeid: String(empId).trim(),
    employee_name: String(
      raw.employee_name ?? raw.employeeName ?? raw.name ?? ""
    ).trim(),
    role: raw.role != null ? String(raw.role) : undefined,
    regular_hr_rate: (() => {
      const fromPayload = Number(raw.regular_hr_rate);
      if (!Number.isNaN(fromPayload) && fromPayload > 0) return fromPayload;
      const rh = Number(raw.regular_hours) || 0;
      const rm = Number(raw.regular_minutes) || 0;
      const regMins = rh * 60 + rm;
      const regAmt = Number(raw.regular_amount) || 0;
      if (regMins > 0 && regAmt > 0) {
        return Math.round((regAmt / (regMins / 60)) * 100) / 100;
      }
      return undefined;
    })(),
    man_hr_rate: Number(raw.man_hr_rate) || 0,
    regular_hours: Number(raw.regular_hours) || 0,
    regular_minutes: Number(raw.regular_minutes) || 0,
    overtime_hours: Number(raw.overtime_hours) || 0,
    overtime_minutes: Number(raw.overtime_minutes) || 0,
    regular_amount: Number(raw.regular_amount) || undefined,
    overtime_amount: Number(raw.overtime_amount) || undefined,
    amount: Number(raw.amount) || 0,
    allocated_cost:
      Number(raw.allocated_cost) || Number(raw.amount) || 0,
  };
}
